import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, Globe } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

interface CropCategory {
  commodity: string | null;
  variety: string | null;
  group: string | null;
  Country: string | null;
  Region: string | null;
}

const SAMPLE_DATA_BY_COUNTRY = {
  "United States": [
    { commodity: "Corn", variety: "Sweet Corn", group: "Grains", country: "United States", region: "Iowa" },
    { commodity: "Tomato", variety: "Roma", group: "Vegetables", country: "United States", region: "California" },
    { commodity: "Apple", variety: "Gala", group: "Fruits", country: "United States", region: "Washington" },
    { commodity: "Wheat", variety: "Winter Wheat", group: "Grains", country: "United States", region: "Kansas" },
    { commodity: "Soybean", variety: "Roundup Ready", group: "Legumes", country: "United States", region: "Illinois" },
  ],
  "India": [
    { commodity: "Rice", variety: "Basmati", group: "Grains", country: "India", region: "Punjab" },
    { commodity: "Tea", variety: "Assam", group: "Beverages", country: "India", region: "Assam" },
    { commodity: "Turmeric", variety: "Curcuma", group: "Spices", country: "India", region: "Telangana" },
    { commodity: "Mango", variety: "Alphonso", group: "Fruits", country: "India", region: "Maharashtra" },
    { commodity: "Cotton", variety: "Bt Cotton", group: "Fiber", country: "India", region: "Gujarat" },
  ],
  "Brazil": [
    { commodity: "Coffee", variety: "Arabica", group: "Beverages", country: "Brazil", region: "Minas Gerais" },
    { commodity: "Sugarcane", variety: "SP80-1816", group: "Industrial Crops", country: "Brazil", region: "Sao Paulo" },
    { commodity: "Soybean", variety: "GMO Soybean", group: "Legumes", country: "Brazil", region: "Mato Grosso" },
    { commodity: "Orange", variety: "Valencia", group: "Fruits", country: "Brazil", region: "Sao Paulo" },
    { commodity: "Cassava", variety: "Manihot", group: "Root Vegetables", country: "Brazil", region: "Pará" },
  ],
  "China": [
    { commodity: "Rice", variety: "Japonica", group: "Grains", country: "China", region: "Heilongjiang" },
    { commodity: "Wheat", variety: "Spring Wheat", group: "Grains", country: "China", region: "Henan" },
    { commodity: "Tea", variety: "Green Tea", group: "Beverages", country: "China", region: "Zhejiang" },
    { commodity: "Cabbage", variety: "Napa Cabbage", group: "Vegetables", country: "China", region: "Shandong" },
    { commodity: "Pork", variety: "Yorkshire", group: "Livestock", country: "China", region: "Sichuan" },
  ],
  "Australia": [
    { commodity: "Wheat", variety: "Durum", group: "Grains", country: "Australia", region: "New South Wales" },
    { commodity: "Barley", variety: "Malting Barley", group: "Grains", country: "Australia", region: "Western Australia" },
    { commodity: "Beef", variety: "Angus", group: "Livestock", country: "Australia", region: "Queensland" },
    { commodity: "Wool", variety: "Merino", group: "Fiber", country: "Australia", region: "Victoria" },
    { commodity: "Macadamia", variety: "Integrifolia", group: "Tree Nuts", country: "Australia", region: "New South Wales" },
  ],
  "Canada": [
    { commodity: "Canola", variety: "Roundup Ready", group: "Oilseeds", country: "Canada", region: "Saskatchewan" },
    { commodity: "Wheat", variety: "Hard Red Spring", group: "Grains", country: "Canada", region: "Alberta" },
    { commodity: "Maple Syrup", variety: "Grade A", group: "Tree Products", country: "Canada", region: "Quebec" },
    { commodity: "Blueberry", variety: "Lowbush", group: "Berries", country: "Canada", region: "Nova Scotia" },
    { commodity: "Lentil", variety: "Red Lentil", group: "Legumes", country: "Canada", region: "Saskatchewan" },
  ],
  "France": [
    { commodity: "Grape", variety: "Chardonnay", group: "Fruits", country: "France", region: "Burgundy" },
    { commodity: "Wheat", variety: "Soft Wheat", group: "Grains", country: "France", region: "Île-de-France" },
    { commodity: "Cheese", variety: "Camembert", group: "Dairy", country: "France", region: "Normandy" },
    { commodity: "Lavender", variety: "Lavandula", group: "Herbs", country: "France", region: "Provence" },
    { commodity: "Sunflower", variety: "Oil Sunflower", group: "Oilseeds", country: "France", region: "Occitanie" },
  ],
};

export const CropCategoriesByCountry = () => {
  const [cropsByCountry, setCropsByCountry] = useState<Record<string, CropCategory[]>>({});
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCropsByCountry();
  }, []);

  const fetchCropsByCountry = async () => {
    try {
      const { data, error } = await supabase
        .from('Crop Categories')
        .select('commodity, variety, group, Country, Region')
        .order('Country', { ascending: true })
        .order('commodity', { ascending: true });

      if (error) throw error;

      // Group by country
      const grouped = (data || []).reduce((acc: Record<string, CropCategory[]>, item) => {
        const country = item.Country || 'Unknown';
        if (!acc[country]) acc[country] = [];
        acc[country].push(item);
        return acc;
      }, {});

      setCropsByCountry(grouped);
    } catch (error) {
      console.error('Error fetching crops:', error);
      toast({
        title: "Error",
        description: "Failed to load crop categories",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadSampleData = (country: string) => {
    const sampleData = SAMPLE_DATA_BY_COUNTRY[country as keyof typeof SAMPLE_DATA_BY_COUNTRY];
    if (!sampleData) return;

    const csvHeader = "commodity,variety,group,country,region\n";
    const csvData = sampleData
      .map(row => `${row.commodity},${row.variety},${row.group},${row.country},${(row as any).region || ''}`)
      .join('\n');
    
    const blob = new Blob([csvHeader + csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crop_categories_${country.toLowerCase().replace(' ', '_')}_sample.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Download Started",
      description: `Sample crop data for ${country} has been downloaded.`,
    });
  };

  const countries = Object.keys(cropsByCountry).filter(country => country !== 'Unknown');
  const sampleCountries = Object.keys(SAMPLE_DATA_BY_COUNTRY);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading crop categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Existing Data Browser */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Browse Existing Crop Categories by Country
          </CardTitle>
          <CardDescription>
            View crop categories that are already in your database, organized by country.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {countries.length > 0 ? (
            <>
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a country to view its crop categories" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country} ({cropsByCountry[country].length} categories)
                    </SelectItem>
                  ))}</SelectContent>
              </Select>

              {selectedCountry && cropsByCountry[selectedCountry] && (
                <div className="space-y-2">
                  <h4 className="font-medium">
                    Crop Categories for {selectedCountry} ({cropsByCountry[selectedCountry].length} total)
                  </h4>
                  <div className="grid gap-2 max-h-64 overflow-y-auto">
                    {cropsByCountry[selectedCountry].map((crop, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 border rounded text-sm">
                        <Badge variant="secondary">{crop.group || 'No Group'}</Badge>
                        {crop.Region && (
                          <Badge variant="outline" className="ml-1">{crop.Region}</Badge>
                        )}
                        <span className="font-medium">{crop.commodity || 'No Commodity'}</span>
                        {crop.variety && (
                          <span className="text-gray-600">- {crop.variety}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-600">No crop categories found. Use the sample data below to get started.</p>
          )}
        </CardContent>
      </Card>

      {/* Sample Data Downloads */}
      <Card>
        <CardHeader>
          <CardTitle>Download Sample Crop Data</CardTitle>
          <CardDescription>
            Get pre-configured CSV files with common crop categories for different countries. 
            Download and import these to quickly populate your database.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sampleCountries.map((country) => (
              <Card key={country} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">{country}</h4>
                  <Badge variant="outline">
                    {SAMPLE_DATA_BY_COUNTRY[country as keyof typeof SAMPLE_DATA_BY_COUNTRY].length} crops
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Sample includes: {SAMPLE_DATA_BY_COUNTRY[country as keyof typeof SAMPLE_DATA_BY_COUNTRY]
                    .slice(0, 3)
                    .map(item => item.commodity)
                    .join(', ')}
                  {SAMPLE_DATA_BY_COUNTRY[country as keyof typeof SAMPLE_DATA_BY_COUNTRY].length > 3 && '...'}
                </p>
                <Button 
                  onClick={() => downloadSampleData(country)}
                  size="sm" 
                  className="w-full"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV
                </Button>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
